import React, { useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'

import { changeAmountOfPages, changeSourcePage } from '../../store/common/commonSlice'
import { RootState } from '../../store/rootReducer'

interface Props {
    children: JSX.Element
    currentPage: number
    setCurrentPage: React.Dispatch<number>
    sourcePage: string
    list: unknown[]
    clearList: unknown
    fetchList: unknown
    amount_of_items_on_page?: number
    lastElement?: React.RefObject<HTMLElement>
    loading?: boolean
    total?: number
    isNecessaryToDisableFirstLoading?: boolean
}

const PaginationAndEndlessScrollWrapper: React.FunctionComponent<Props> = ({
    children,
    currentPage,
    setCurrentPage,
    sourcePage,
    list,
    clearList,
    fetchList,
    amount_of_items_on_page = 20,
    lastElement,
    loading,
    total,
    isNecessaryToDisableFirstLoading = false
}: Props) => {
    const dispatch = useDispatch()
    const location = useLocation()

    const isPageScrolled = useSelector<RootState, boolean>((state) => state.common.isPageScrolled)

    const pageFromAdress = () => {
        if (location.search && new URLSearchParams(location.search).get('p'))
            return Number(new URLSearchParams(location.search).get('p'))
        return undefined
    }

    const firstLoad = useRef(true)
    const observer = useRef(null)

    // beginning of pagination logic
    useEffect(() => {
        if (pageFromAdress()) {
            setCurrentPage(pageFromAdress())
            dispatch(changeSourcePage({ source: sourcePage }))
            dispatch(changeAmountOfPages({ page: pageFromAdress() }))
        }
    }, [location])

    useEffect(() => {
        if (pageFromAdress()) {
            if (firstLoad.current) {
                firstLoad.current = false
                //This 'if' fixes the bug of displaying selections from the first page and the current one at the same time when switching to a numbered page via a link
            } else {
                dispatch(clearList)

                fetchList && dispatch(fetchList)
            }
        }
    }, [currentPage])
    // end of pagination logic

    // beginning of infinity scroll logic

    useEffect(() => {
        if (!pageFromAdress() && !list.length) {
            setCurrentPage(1)
            dispatch(changeAmountOfPages({ page: 1 }))
        }
    }, [])

    useEffect(() => {
        if (window && 'IntersectionObserver' in window) {
            if (!pageFromAdress()) {
                if (loading) return
                if (observer.current) observer.current.disconnect()
                if (lastElement.current) {
                    // @ts-ignore
                    const callback = function (entries) {
                        if (
                            entries[0].isIntersecting &&
                            currentPage * amount_of_items_on_page < total &&
                            window.navigator.onLine &&
                            (isPageScrolled || !isNecessaryToDisableFirstLoading)
                        ) {
                            setCurrentPage(currentPage + 1)
                            dispatch(changeAmountOfPages({ page: currentPage + 1 }))
                            dispatch(changeSourcePage({ source: sourcePage }))
                            dispatch(fetchList)
                        }
                    }
                    observer.current = new IntersectionObserver(callback)
                    observer.current.observe(lastElement.current)
                }
            }
        }
    }, [loading, isPageScrolled])

    return <>{children}</>
}

export default PaginationAndEndlessScrollWrapper
